// React
import { memo } from "react";

// Servisni sloj
import { formatUnitPrice } from "services/api/pricing/consts";

// Lokalne komponente
import { MetricLabel } from "../common/CardHeader";
import DashboardTooltip, {
  TooltipDivider,
  TooltipRow,
  TooltipTitle,
} from "../common/DashboardTooltip";
import { num } from "../format";

// Tipovi
import { KpiCardsProps, StatCardProps } from "./types";

const StatCard: React.FC<StatCardProps> = (props) => {
  const { label, value, hint, accent = "text-slate-900", tooltip } = props;

  return (
    <DashboardTooltip
      position="top-center"
      trigger={
        <div className="bg-white border border-slate-200 rounded-sm px-4 py-4 w-full h-full cursor-help hover:border-slate-300 transition-colors">
          <MetricLabel>{label}</MetricLabel>
          <div className={`text-2xl font-bold mt-1 ${accent}`}>{value}</div>
          {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
      }
    >
      <div className="max-w-[240px]">{tooltip}</div>
    </DashboardTooltip>
  );
};

/** Zbrojevi kroz sve klijente. Sve brojke dolaze iz baze. */
const KpiCards: React.FC<KpiCardsProps> = (props: KpiCardsProps) => {
  const { kpis } = props;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      <StatCard
        label="Klijenti"
        value={num(kpis.tenantsTotal)}
        tooltip={
          <>
            <TooltipTitle>Klijenti</TooltipTitle>
            <TooltipRow label="Ukupno" value={num(kpis.tenantsTotal)} />
            <TooltipDivider />
            <TooltipRow label="U cjenovnoj grupi" value={num(kpis.tenantsOnScheme)} />
          </>
        }
      />

      <StatCard
        label="Vozila"
        value={num(kpis.vehiclesTotal)}
        hint={`${num(kpis.vehiclesPublished)} objavljenih`}
        tooltip={
          <>
            <TooltipTitle>Vozila</TooltipTitle>
            <TooltipRow label="Objavljena" value={num(kpis.vehiclesPublished)} />
            <TooltipRow label="Nacrti" value={num(kpis.vehiclesDraft)} />
            <TooltipDivider />
            <TooltipRow label="Ukupno" value={num(kpis.vehiclesTotal)} />
          </>
        }
      />

      <StatCard
        label="Korisnici"
        value={num(kpis.usersTotal)}
        tooltip={
          <>
            <TooltipTitle>Korisnici</TooltipTitle>
            <TooltipRow label="Ukupno" value={num(kpis.usersTotal)} />
          </>
        }
      />

      <StatCard
        label="Računi"
        value={num(kpis.invoicesOutgoing + kpis.invoicesIncoming)}
        hint={`${num(kpis.invoicesOutgoing)} izlaznih`}
        tooltip={
          <>
            <TooltipTitle>Računi klijenata</TooltipTitle>
            <TooltipRow label="Izlazni" value={num(kpis.invoicesOutgoing)} />
            <TooltipRow label="Ulazni" value={num(kpis.invoicesIncoming)} />
            <TooltipDivider />
            {/* Bez ovoga se lako pročita kao naša naplata. */}
            <TooltipRow label="Promet klijenata" value="ne naša naplata" />
          </>
        }
      />

      <StatCard
        label="Minimum mjesečno"
        value={formatUnitPrice.format(kpis.minimumMonthlyTotal)}
        hint="donja granica naplate"
        tooltip={
          <>
            <TooltipTitle>Minimalna naknada</TooltipTitle>
            <TooltipRow
              label="Zbroj minimuma"
              value={formatUnitPrice.format(kpis.minimumMonthlyTotal)}
            />
            <TooltipDivider />
            <TooltipRow label="Razriješeno" value="kroz grupu i posebne uvjete" />
          </>
        }
      />

      <StatCard
        label="U grupi"
        value={num(kpis.tenantsOnScheme)}
        hint={`od ukupno ${num(kpis.tenantsTotal)}`}
        tooltip={
          <>
            <TooltipTitle>Cjenovne grupe</TooltipTitle>
            <TooltipRow label="Klijenata u grupi" value={num(kpis.tenantsOnScheme)} />
            <TooltipRow
              label="Po standardnom cjeniku"
              value={num(kpis.tenantsTotal - kpis.tenantsOnScheme)}
            />
          </>
        }
      />
    </div>
  );
};

export default memo(KpiCards);
